/**
 * Sentinel route options, drawn on the mission map while a card is hovered.
 *
 * The card is text. The decision is about a road. Hovering lights that line
 * (recommended teal, others ghosted) so Take it is made against the picture.
 */
import React, { useMemo } from 'react';
import { Layer, Source } from 'react-map-gl';
import { RouteOption } from '../../../services/missionApi';
import { isRecommended, optionLabel } from '../chatView';

const TEAL = '#00b8a9';
const GHOST = '#8fa3bf';
const NS = 'sentinel-preview';

export interface RouteOptionPreview {
  options: RouteOption[];
  focus: RouteOption | null;
  recommend?: string | null;
}

export interface MissionOptionPreviewLayerProps {
  preview: RouteOptionPreview | null;
}

const empty: GeoJSON.FeatureCollection = { type: 'FeatureCollection', features: [] };

const MissionOptionPreviewLayer: React.FC<MissionOptionPreviewLayerProps> = ({ preview }) => {
  const data = useMemo<GeoJSON.FeatureCollection>(() => {
    if (!preview?.options.length) return empty;
    return {
      type: 'FeatureCollection',
      features: preview.options
        .filter((option) => option.geometry?.coordinates?.length > 1)
        .map((option, index) => {
          const focused = preview.focus
            ? optionLabel(option) === optionLabel(preview.focus)
            : isRecommended(option, preview.recommend);
          return {
            type: 'Feature' as const,
            id: index,
            properties: { focused: focused ? 1 : 0 },
            geometry: option.geometry,
          };
        }),
    };
  }, [preview]);

  if (!preview || data.features.length === 0) return null;

  return (
    <Source id={`${NS}-src`} type="geojson" data={data}>
      <Layer
        id={`${NS}-ghost`}
        type="line"
        filter={['==', ['get', 'focused'], 0]}
        paint={{
          'line-color': GHOST,
          'line-width': 3,
          'line-opacity': 0.45,
          'line-dasharray': [2, 1.4],
        }}
      />
      <Layer
        id={`${NS}-focus`}
        type="line"
        filter={['==', ['get', 'focused'], 1]}
        paint={{
          'line-color': TEAL,
          'line-width': 5,
          'line-opacity': 0.95,
        }}
      />
    </Source>
  );
};

export default MissionOptionPreviewLayer;
